/**
 * AppSidebar - Navigation sidebar
 *
 * Icon-based navigation for switching between views.
 */

import { useState } from 'react';
import {
  BarChart3,
  Briefcase,
  ClipboardList,
  ShieldAlert,
  Shield,
  Bug,
  Stethoscope,
  MonitorDot,
  Settings,
  LogOut,
  Sun,
  Moon,
  RefreshCw,
  Megaphone,
  GitCommitHorizontal,
  MessageSquarePlus,
  Activity,
} from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { UpdatesModal } from '@/components/layout/UpdatesModal';
import { FeedbackModal } from '@/components/layout/FeedbackModal';
import { VersionModal } from '@/components/layout/VersionModal';
import { CURRENT_VERSION } from '@/lib/infra/changelog';
import { storageKey } from '@/lib/infra/storage';

const buildHash = typeof __BUILD_COMMIT__ !== 'undefined' ? __BUILD_COMMIT__ : 'local';

export type AppView =
  | 'explore'
  | 'nba'
  | 'value-dashboard'
  | 'positions'
  | 'orders'
  | 'position-tracker'
  | 'dev-explorer'
  | 'issues'
  | 'diagnostics'
  | 'dev-dash'
  | 'feedback'
  | 'settings'
  | 'admin'
  | 'my-activity';

// Views removed from nav but kept in type for deep-link / route compat:
// 'explore', 'dev-explorer', 'feedback' (now merged into 'issues')

interface NavItem {
  id: AppView;
  icon: React.ReactNode;
  label: string;
}

const navItems: NavItem[] = [
  { id: 'value-dashboard', icon: <BarChart3 className="h-5 w-5" />, label: 'Value Dashboard' },
  { id: 'positions', icon: <Briefcase className="h-5 w-5" />, label: 'Positions' },
  { id: 'orders', icon: <ClipboardList className="h-5 w-5" />, label: 'Orders' },
  { id: 'position-tracker', icon: <ShieldAlert className="h-5 w-5" />, label: 'Position Tracker' },
  { id: 'issues', icon: <Bug className="h-5 w-5" />, label: 'Issues & Feedback' },
  { id: 'diagnostics', icon: <Stethoscope className="h-5 w-5" />, label: 'Diagnostics' },
  { id: 'dev-dash', icon: <MonitorDot className="h-5 w-5" />, label: 'Dev Dashboard' },
  { id: 'settings', icon: <Settings className="h-5 w-5" />, label: 'Settings' },
];

const IS_MULTI_TENANT = import.meta.env.VITE_MULTI_TENANT === 'true';

interface AppSidebarProps {
  currentView: AppView;
  onViewChange: (view: AppView) => void;
  onDisconnect?: () => void;
  isAdmin?: boolean;
}

export function AppSidebar({ currentView, onViewChange, onDisconnect, isAdmin }: AppSidebarProps) {
  const [showUpdates, setShowUpdates] = useState(false);
  const [showVersion, setShowVersion] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  return (
    <div className="flex h-full flex-col">
      {/* Logo/Brand */}
      <div className="border-border flex h-12 items-center justify-center border-b">
        <span className="text-primary text-lg font-bold">K</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-2">
        {IS_MULTI_TENANT && isAdmin && (
          <NavButton
            icon={<Shield className="h-5 w-5" />}
            label="Admin Panel"
            isActive={currentView === 'admin'}
            onClick={() => onViewChange('admin')}
          />
        )}
        {IS_MULTI_TENANT && (
          <NavButton
            icon={<Activity className="h-5 w-5" />}
            label="My Activity"
            isActive={currentView === 'my-activity'}
            onClick={() => onViewChange('my-activity')}
          />
        )}
        {navItems.map((item) => (
          <NavButton
            key={item.id}
            icon={item.icon}
            label={item.label}
            isActive={currentView === item.id}
            onClick={() => onViewChange(item.id)}
          />
        ))}
      </nav>

      {/* Theme toggle, refresh, updates, and disconnect at bottom */}
      <div className="border-border border-t py-2">
        <ThemeButton />
        <FeedbackButton onClick={() => setShowFeedback(true)} />
        <RefreshCacheButton />
        <VersionButton onClick={() => setShowVersion(true)} />
        <UpdatesButton onClick={() => setShowUpdates(true)} />
        {onDisconnect && (
          <NavButton
            icon={<LogOut className="h-5 w-5" />}
            label="Disconnect"
            onClick={onDisconnect}
            variant="danger"
          />
        )}
      </div>

      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
      />
      <VersionModal
        isOpen={showVersion}
        onClose={() => setShowVersion(false)}
      />
      <UpdatesModal
        isOpen={showUpdates}
        onClose={() => setShowUpdates(false)}
      />
    </div>
  );
}

interface NavButtonProps {
  icon: React.ReactNode;
  label: string;
  isActive?: boolean;
  onClick: () => void;
  variant?: 'default' | 'danger';
}

function NavButton({ icon, label, isActive, onClick, variant = 'default' }: NavButtonProps) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={cn(
        'group relative flex h-12 w-full items-center justify-center transition-colors',
        variant === 'default' &&
          !isActive &&
          'text-muted-foreground hover:text-foreground hover:bg-muted/50',
        variant === 'default' && isActive && 'text-primary bg-primary/10',
        variant === 'danger' &&
          'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
      )}
    >
      {icon}

      {/* Active indicator */}
      {isActive && (
        <div className="bg-primary absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r" />
      )}

      {/* Tooltip */}
      <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        {label}
      </div>
    </button>
  );
}

function ThemeButton() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={cn(
        'group relative flex h-12 w-full items-center justify-center transition-colors',
        'text-muted-foreground hover:text-foreground hover:bg-muted/50'
      )}
    >
      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}

      {/* Tooltip */}
      <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        {isDark ? 'Light mode' : 'Dark mode'}
      </div>
    </button>
  );
}

/** Keys to preserve across cache refresh (credentials + auth) */
const PRESERVE_KEYS = [
  storageKey('kalshi_profiles_v1'),
  storageKey('kalshi_creds_encrypted'),
  storageKey('polymarket_config_v1'),
];

function RefreshCacheButton() {
  const handleRefresh = () => {
    // Save credential values
    const saved: [string, string | null][] = PRESERVE_KEYS.map((k) => [k, localStorage.getItem(k)]);
    // Clear everything
    localStorage.clear();
    // Restore credentials
    for (const [key, value] of saved) {
      if (value !== null) localStorage.setItem(key, value);
    }
    // Navigate to root and hard reload
    window.location.href = import.meta.env.BASE_URL || '/';
  };

  return (
    <button
      onClick={handleRefresh}
      title="Refresh cache"
      className={cn(
        'group relative flex h-12 w-full items-center justify-center transition-colors',
        'text-muted-foreground hover:text-foreground hover:bg-muted/50'
      )}
    >
      <RefreshCw className="h-5 w-5" />

      {/* Tooltip */}
      <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        Refresh cache
      </div>
    </button>
  );
}

function UpdatesButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title={`Updates (${CURRENT_VERSION})`}
      className={cn(
        'group relative flex h-12 w-full items-center justify-center transition-colors',
        'text-muted-foreground hover:text-foreground hover:bg-muted/50'
      )}
    >
      <Megaphone className="h-5 w-5" />

      {/* Tooltip */}
      <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        Updates ({CURRENT_VERSION})
      </div>
    </button>
  );
}

function FeedbackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title="Send Feedback"
      className={cn(
        'group relative flex h-12 w-full items-center justify-center transition-colors',
        'text-muted-foreground hover:text-foreground hover:bg-muted/50'
      )}
    >
      <MessageSquarePlus className="h-5 w-5" />

      {/* Tooltip */}
      <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        Send Feedback
      </div>
    </button>
  );
}

function VersionButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title={`Version (${buildHash})`}
      className={cn(
        'group relative flex h-12 w-full items-center justify-center transition-colors',
        'text-muted-foreground hover:text-foreground hover:bg-muted/50'
      )}
    >
      <GitCommitHorizontal className="h-5 w-5" />

      {/* Tooltip */}
      <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        Version ({buildHash})
      </div>
    </button>
  );
}
